// Admin — SLA por Seller: lista os sellers com o prazo de envio em DIAS, edição rápida inline.
// Salvar aplica o prazo a TODAS as regiões do seller (a matriz Regiões & SLAs faz o ajuste fino).
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check } from 'lucide-react';
import { getSlaMatrix, salvarSlaSeller, SlaMatrix } from '../api';
import Dica from '../components/Dica';

const AJUDA_SLA = 'Prazo (em dias) que o seller tem para ENVIAR o pedido após o recebimento — é a régua do problema P1 (pedido parado/atrasado) e do "% no Prazo". Salvar aplica o prazo a todas as regiões do seller de uma vez; para prazos diferentes por região, use a tela Regiões & SLAs. Sellers sem SLA configurado usam o default global de 3 dias corridos.';

export default function AdminSlaSellers() {
  const [data, setData] = useState<SlaMatrix | null>(null);
  const [busca, setBusca] = useState('');
  const [dias, setDias] = useState<Record<number, string>>({});
  const [salvando, setSalvando] = useState<number | null>(null);
  const [salvos, setSalvos] = useState<Set<number>>(new Set());

  const load = () => getSlaMatrix().then(setData);
  useEffect(() => { load(); }, []);

  // situação atual por seller: uniforme (X dias), misto, ou default
  const situacao = useMemo(() => {
    const map = new Map<number, { texto: string; uniformeDias: number | null; configuradas: number }>();
    if (!data) return map;
    for (const s of data.sellers) {
      const doSeller = data.slas.filter((x) => x.seller_id === s.seller_id);
      const prazos = Array.from(new Set(doSeller.map((x) => x.prazo_horas)));
      if (prazos.length === 0) {
        map.set(s.seller_id, { texto: `${data.default_horas / 24} dias (default global)`, uniformeDias: null, configuradas: 0 });
      } else if (prazos.length === 1) {
        map.set(s.seller_id, { texto: `${prazos[0] / 24} dias`, uniformeDias: prazos[0] / 24, configuradas: doSeller.length });
      } else {
        map.set(s.seller_id, { texto: `misto (${prazos.map((p) => p / 24).sort((a, b) => a - b).join(' / ')} dias)`, uniformeDias: null, configuradas: doSeller.length });
      }
    }
    return map;
  }, [data]);

  if (!data) return <p className="empty">Carregando SLAs…</p>;

  const salvar = async (sellerId: number) => {
    const d = Number(dias[sellerId]);
    if (!d || d <= 0) return;
    setSalvando(sellerId);
    try {
      await salvarSlaSeller(sellerId, d);
      await load();
      setDias((v) => ({ ...v, [sellerId]: '' }));
      setSalvos((s) => new Set(s).add(sellerId));
      setTimeout(() => setSalvos((s) => { const n = new Set(s); n.delete(sellerId); return n; }), 2500);
    } finally {
      setSalvando(null);
    }
  };

  const lista = data.sellers.filter((s) => !busca
    || s.nome_fantasia?.toLowerCase().includes(busca.toLowerCase()));

  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="flex items-center gap-2">SLA por Seller <Dica titulo="SLA de envio" texto={AJUDA_SLA} /></h1>
          <div className="sub">
            Prazo de envio em dias, aplicado a todas as regiões do seller ·{' '}
            <Link to="/service-level/admin/regioes-slas">ajuste fino por região →</Link>
          </div>
        </div>
        <div className="filters">
          <input placeholder="Buscar seller…" value={busca} onChange={(e) => setBusca(e.target.value)} />
        </div>
      </div>

      <div className="card">
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Seller</th>
                <th>UF</th>
                <th>SLA atual</th>
                <th className="num">Regiões configuradas</th>
                <th>Novo prazo (dias)</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {lista.map((s) => {
                const sit = situacao.get(s.seller_id)!;
                return (
                  <tr key={s.seller_id}>
                    <td><strong>{s.nome_fantasia}</strong></td>
                    <td>{s.uf_base}</td>
                    <td>
                      {sit.configuradas === 0
                        ? <span className="badge default-sla">{sit.texto}</span>
                        : sit.texto.startsWith('misto')
                          ? <span className="badge p1">{sit.texto}</span>
                          : <span className="badge FINALIZADO">{sit.texto}</span>}
                    </td>
                    <td className="num">{sit.configuradas}/{data.regioes.length}</td>
                    <td>
                      <input
                        type="number" min="1" step="1" placeholder={sit.uniformeDias ? String(sit.uniformeDias) : 'ex.: 3'}
                        className="w-24 px-2.5 py-[7px] border border-grid rounded-lg bg-surface text-[13px]"
                        value={dias[s.seller_id] ?? ''}
                        onChange={(e) => setDias((v) => ({ ...v, [s.seller_id]: e.target.value }))}
                        onKeyDown={(e) => { if (e.key === 'Enter') salvar(s.seller_id); }}
                      />
                    </td>
                    <td>
                      {salvos.has(s.seller_id)
                        ? <span className="delta-good inline-flex items-center gap-1 text-xs font-semibold"><Check size={14} /> salvo</span>
                        : (
                          <button
                            className="btn secondary"
                            onClick={() => salvar(s.seller_id)}
                            disabled={salvando === s.seller_id || !Number(dias[s.seller_id])}
                          >
                            {salvando === s.seller_id ? 'Salvando…' : 'Aplicar'}
                          </button>
                        )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {lista.length === 0 && <p className="empty">Nenhum seller encontrado.</p>}
        </div>
        <p className="chart-note">
          Alterações criam nova vigência — pedidos passados continuam avaliados contra o SLA vigente na época.
          Prazo em dias corridos; para dias úteis, use a matriz por região.
        </p>
      </div>
    </>
  );
}
